import { ThreatTypes } from '../helpers/types';

export const THREAT_EXPORT_HEADERS = [
  'company_id',
  'protected_asset_id',
  'protected_asset_name',
  'threat_id',
  'threat_url',
  'detection_date',
  'identification_status',
  'identification_status_updated_at',
  'source',
  'resurgent_threat',
] as const;

type ThreatExportRow = Array<string | number | boolean>;

type ExportableThreat = Pick<
  ThreatTypes,
  | 'company_id'
  | 'protected_asset_id'
  | 'protected_asset_name'
  | 'threat_id'
  | 'detection_date'
  | 'identification_status'
  | 'value'
  | 'source'
> & {
  resurgent_threat?: boolean | string | null;
};

export const escapeCsvCell = (cell: string | number | boolean | null | undefined) => {
  const value = cell == null ? '' : String(cell);

  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

export const buildCsvContent = (
  headers: readonly string[],
  rows: ThreatExportRow[],
) =>
  [headers, ...rows]
    .map(row => row.map(cell => escapeCsvCell(cell)).join(','))
    .join('\n');

export const mapThreatsToExportRows = (threats: ExportableThreat[]): ThreatExportRow[] =>
  threats.map(threat => [
    threat.company_id ?? '',
    threat.protected_asset_id ?? '',
    threat.protected_asset_name ?? '',
    threat.threat_id ?? '',
    threat.value?.value ?? '',
    threat.detection_date ?? '',
    threat.identification_status?.value ?? '',
    threat.identification_status?.updated_at ?? '',
    threat.source ?? '',
    threat.resurgent_threat ?? '',
  ]);

export const getThreatExportFilename = (date = new Date()) =>
  `threats_export_${date.toISOString().split('T')[0]}.csv`;

export const downloadCsvFile = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportThreatsToCsv = (
  threats: ExportableThreat[],
  date = new Date(),
) => {
  const csvContent = buildCsvContent(
    THREAT_EXPORT_HEADERS,
    mapThreatsToExportRows(threats),
  );

  downloadCsvFile(csvContent, getThreatExportFilename(date));
};
